<script>
    import { SendRequest } from "./APIService";
    import { showModal } from "./Modal";
    import moment from "moment";

    let readyAnswers = [];

    // Enums — must match backend C# CRMStatus, CRMCategory, CRMPriority ordering
    const STATUS = [
        { value: 2, label: "UnderReview", color: "primary" },
        { value: 1, label: "NeedMoreInfo", color: "warning" },
        { value: 3, label: "WaitForUserResponse", color: "info" },
        { value: 0, label: "Closed", color: "secondary" },
    ];
    const CATEGORY = [
        { value: 0, label: "Payment" },
        { value: 1, label: "RecoveryAccount" },
        { value: 2, label: "Technical" },
        { value: 3, label: "ReportUser" },
        { value: 4, label: "Suggestion" },
        { value: 5, label: "Others" },
    ];
    const PRIORITY = [
        { value: 0, label: "High", color: "danger" },
        { value: 1, label: "Low", color: "success" },
    ];
    const MARKET = {
        0: "Cafe Bazaar",
        1: "Google Play",
        2: "MyKet",
        3: "Direct",
    };

    // State
    let stats = {
        underReview: 0,
        needMoreInfo: 0,
        waitForUserResponse: 0,
        closed: 0,
    };
    let tickets = [];
    let selectedId = null;
    let detail = null;

    let filterMode = "status"; // "status" | "category"
    let activeStatus = 0;
    let activeCategory = 0;
    let page = 1;
    const PAGE_SIZE = 20;

    let replyText = "";
    let sendEmail = false;
    let newStatus = "0";

    // ── API calls ──────────────────────────────────────────────────────────────

    function loadStats() {
        SendRequest("crmStats", "GET", "", (data) => {
            stats = data;
        });
    }

    function loadByStatus(status, p = 1) {
        activeStatus = status;
        filterMode = "status";
        page = p;
        clearDetail();
        loadStats();
        SendRequest(
            "crmTicketsByStatus",
            "GET",
            `status=${status}&page=${p}&pageSize=${PAGE_SIZE}`,
            (data) => {
                tickets = data ?? [];
            },
        );
    }

    function loadByCategory(cat, p = 1) {
        activeCategory = cat;
        filterMode = "category";
        page = p;
        clearDetail();
        loadStats();
        SendRequest(
            "crmTicketsByCategory",
            "GET",
            `category=${cat}&page=${p}&pageSize=${PAGE_SIZE}`,
            (data) => {
                tickets = data ?? [];
            },
        );
    }

    function selectTicket(id) {
        selectedId = id;
        detail = null;
        readyAnswers = [];
        replyText = "";
        sendEmail = false;

        loadStats();

        SendRequest("crmTicket", "GET", `ticketId=${id}`, (data) => {
            detail = data;
            newStatus = String(data.status);

            SendRequest(
                "CRMReadyAnswer",
                "GET",
                `marketType=${data.marketType}&category=${data.category}`,
                (ready) => {
                    readyAnswers = ready?.answers ?? [];
                },
                () => (readyAnswers = []),
            );
        });
    }

    function clearDetail() {
        selectedId = null;
        detail = null;
    }

    function sendReply() {
        if (!replyText.trim()) {
            showModal("Error", "Reply cannot be empty", "OK");
            return;
        }
        SendRequest(
            "crmTicketReply",
            "POST",
            `ticketId=${selectedId}`,
            () => {
                replyText = "";
                sendEmail = false;
                showModal("Done", "Reply sent successfully", "OK");
                selectTicket(selectedId);
            },
            (error) => showModal("Error", error, "OK"),
            undefined,
            { Description: replyText, IsNeededEmail: sendEmail },
        );
    }

    function closeTicket() {
        SendRequest(
            "crmTicketClose",
            "POST",
            `ticketId=${selectedId}`,
            () => {
                showModal("Done", "Ticket closed", "OK");
                if (filterMode === "status") loadByStatus(activeStatus, page);
                else loadByCategory(activeCategory, page);
            },
            (error) => showModal("Error", error, "OK"),
            undefined,
            {},
        );
    }

    function changeStatus() {
        SendRequest(
            "crmTicketChangeStatus",
            "POST",
            `ticketId=${selectedId}`,
            () => {
                showModal("Done", "Status updated", "OK");
                selectTicket(selectedId);
            },
            (error) => showModal("Error", error, "OK"),
            undefined,
            { Status: parseInt(newStatus) },
        );
    }

    function deleteTicketsByStatus() {
        const label = statusOf(activeStatus).label;
        const confirmAction = confirm(
            `Are you sure to remove ALL crm tickets with status "${label}" from database? This can not be undone.`,
        );
        if (!confirmAction) return;

        SendRequest(
            "crmDeleteTicketsByStatus",
            "POST",
            `status=${activeStatus}`,
            (data) => {
                // Every ticket of this status is gone, so the list is empty by
                // definition — the delete response carries the fresh stats, so
                // there is nothing left to re-fetch.
                page = 1;
                tickets = [];
                clearDetail();
                if (data?.stats) stats = data.stats;

                showModal(
                    "Done",
                    `Deleted ${data?.deletedCount ?? 0} tickets with status "${label}"`,
                    "OK",
                );
            },
            (error) => showModal("Error", error, "OK"),
            undefined,
            {},
        );
    }

    function refreshList() {
        if (filterMode === "status") loadByStatus(activeStatus, page);
        else loadByCategory(activeCategory, page);
    }

    // ── Helpers ────────────────────────────────────────────────────────────────

    function fmt(d) {
        return d ? moment(d).format("YYYY-MM-DD HH:mm") : "—";
    }
    function ago(d) {
        return d ? moment(d).fromNow() : "—";
    }

    function statusOf(v) {
        return (
            STATUS.find((s) => s.value === v) ?? {
                label: String(v),
                color: "secondary",
            }
        );
    }
    function priorityOf(v) {
        return (
            PRIORITY.find((p) => p.value === v) ?? {
                label: String(v),
                color: "secondary",
            }
        );
    }
    function categoryOf(v) {
        return CATEGORY.find((c) => c.value === v) ?? { label: String(v) };
    }

    // ── Init ───────────────────────────────────────────────────────────────────
    loadStats();
    loadByStatus(0);
</script>

<!-- ═══════════════════════════════════════════════════════════════════════════ -->
<!--  Stats Bar                                                                 -->
<!-- ═══════════════════════════════════════════════════════════════════════════ -->
<div class="row g-3 mb-3">
    <div class="col-md-3">
        <div
            class="stat-card border-primary clickable"
            on:click={() => loadByStatus(2)}
        >
            <span class="stat-num text-primary">{stats.underReview}</span>
            <span class="stat-lbl">Under Review</span>
        </div>
    </div>
    <div class="col-md-3">
        <div
            class="stat-card border-warning clickable"
            on:click={() => loadByStatus(1)}
        >
            <span class="stat-num text-warning">{stats.needMoreInfo}</span>
            <span class="stat-lbl">Need More Info</span>
        </div>
    </div>
    <div class="col-md-3">
        <div
            class="stat-card border-info clickable"
            on:click={() => loadByStatus(3)}
        >
            <span class="stat-num text-info">{stats.waitForUserResponse}</span>
            <span class="stat-lbl">Wait for Response</span>
        </div>
    </div>
    <div class="col-md-3">
        <div
            class="stat-card border-secondary clickable"
            on:click={() => loadByStatus(0)}
        >
            <span class="stat-num text-secondary">{stats.closed}</span>
            <span class="stat-lbl">Closed</span>
        </div>
    </div>
</div>

<!-- ═══════════════════════════════════════════════════════════════════════════ -->
<!--  Main layout                                                               -->
<!-- ═══════════════════════════════════════════════════════════════════════════ -->
<div class="row g-3">
    <!-- ── LEFT: Ticket list ─────────────────────────────────────────────── -->
    <div class="col-md-4">
        <div class="card h-100">
            <!-- Filter tabs -->
            <div class="card-header p-0">
                <ul class="nav nav-tabs mb-0 border-0">
                    <li class="nav-item">
                        <button
                            class="nav-link {filterMode === 'status'
                                ? 'active'
                                : ''}"
                            on:click={() => {
                                filterMode = "status";
                                loadByStatus(activeStatus);
                            }}
                        >
                            By Status
                        </button>
                    </li>
                    <li class="nav-item">
                        <button
                            class="nav-link {filterMode === 'category'
                                ? 'active'
                                : ''}"
                            on:click={() => {
                                filterMode = "category";
                                loadByCategory(activeCategory);
                            }}
                        >
                            By Category
                        </button>
                    </li>
                    <li class="ms-auto d-flex align-items-center pe-2">
                        <button
                            class="btn btn-sm btn-outline-secondary"
                            title="Refresh"
                            on:click={refreshList}>↻</button
                        >
                    </li>
                </ul>
            </div>

            <!-- Sub-filter buttons -->
            <div class="px-2 pt-2 pb-1 border-bottom">
                {#if filterMode === "status"}
                    <div class="d-flex flex-wrap gap-1">
                        {#each STATUS as s}
                            <button
                                class="btn btn-sm {activeStatus === s.value
                                    ? 'btn-' + s.color
                                    : 'btn-outline-' + s.color}"
                                style="font-size:0.72rem; padding:2px 7px"
                                on:click={() => loadByStatus(s.value)}
                            >
                                {s.label}
                            </button>
                        {/each}
                        <button
                            class="btn btn-sm btn-danger ms-auto"
                            style="font-size:0.72rem; padding:2px 7px"
                            title="Delete all tickets with the selected status"
                            on:click={deleteTicketsByStatus}
                        >
                            🗑 Delete "{statusOf(activeStatus).label}"
                        </button>
                    </div>
                {:else}
                    <div class="d-flex flex-wrap gap-1">
                        {#each CATEGORY as c}
                            <button
                                class="btn btn-sm {activeCategory === c.value
                                    ? 'btn-dark'
                                    : 'btn-outline-dark'}"
                                style="font-size:0.72rem; padding:2px 7px"
                                on:click={() => loadByCategory(c.value)}
                            >
                                {c.label}
                            </button>
                        {/each}
                    </div>
                {/if}
            </div>

            <!-- Ticket items -->
            <div class="overflow-auto" style="flex:1">
                {#if tickets.length === 0}
                    <div class="text-center text-muted py-5">
                        No tickets found
                    </div>
                {:else}
                    <div class="list-group list-group-flush">
                        {#each tickets as t (t.id)}
                            <button
                                class="list-group-item list-group-item-action ticket-item p-2
                                       {selectedId === t.id ? 'active' : ''}"
                                on:click={() => selectTicket(t.id)}
                            >
                                <div
                                    class="d-flex justify-content-between align-items-start"
                                >
                                    <div class="ticket-title">{t.title}</div>
                                    <span
                                        class="badge bg-{priorityOf(t.priority)
                                            .color} ms-1 flex-shrink-0"
                                        style="font-size:0.65rem"
                                    >
                                        {priorityOf(t.priority).label}
                                    </span>
                                </div>

                                <div
                                    class="d-flex justify-content-between align-items-center mt-1"
                                >
                                    <span
                                        class="badge bg-{statusOf(t.status)
                                            .color}"
                                        style="font-size:0.65rem"
                                    >
                                        {statusOf(t.status).label}
                                    </span>
                                    <span class="ticket-meta"
                                        >{ago(t.createdAt)}</span
                                    >
                                </div>

                                <div class="ticket-meta mt-1">
                                    #{t.id?.slice(-8)} · {categoryOf(t.category)
                                        .label} · {t.chatCount} msg{t.chatCount !==
                                    1
                                        ? "s"
                                        : ""}
                                </div>
                            </button>
                        {/each}
                    </div>
                {/if}
            </div>

            <!-- Pagination -->
            <div
                class="card-footer d-flex justify-content-between align-items-center py-1 px-2"
            >
                <button
                    class="btn btn-sm btn-outline-secondary"
                    disabled={page <= 1}
                    on:click={() =>
                        filterMode === "status"
                            ? loadByStatus(activeStatus, page - 1)
                            : loadByCategory(activeCategory, page - 1)}
                >
                    ← Prev
                </button>
                <span class="text-muted" style="font-size:0.8rem"
                    >Page {page}</span
                >
                <button
                    class="btn btn-sm btn-outline-secondary"
                    disabled={tickets.length < PAGE_SIZE}
                    on:click={() =>
                        filterMode === "status"
                            ? loadByStatus(activeStatus, page + 1)
                            : loadByCategory(activeCategory, page + 1)}
                >
                    Next →
                </button>
            </div>
        </div>
    </div>

    <!-- ── RIGHT: Ticket detail ───────────────────────────────────────────── -->
    <div class="col-md-8">
        {#if !selectedId}
            <div class="card">
                <div class="card-body text-center text-muted py-5">
                    <div style="font-size:2.5rem">🎫</div>
                    <p class="mt-2 mb-0">Select a ticket to view details</p>
                </div>
            </div>
        {:else if !detail}
            <div class="card">
                <div class="card-body text-center text-muted py-5">
                    Loading…
                </div>
            </div>
        {:else}
            <!-- Ticket header -->
            <div class="card mb-3">
                <div
                    class="card-header d-flex justify-content-between align-items-center flex-wrap gap-2"
                >
                    <div>
                        <h5 class="mb-0">{detail.title}</h5>
                        <small class="text-muted font-monospace"
                            >#{detail.id}</small
                        >
                    </div>
                    <div class="d-flex gap-2">
                        <span
                            class="badge bg-{priorityOf(detail.priority).color}"
                        >
                            {priorityOf(detail.priority).label} Priority
                        </span>
                        <span class="badge bg-{statusOf(detail.status).color}">
                            {statusOf(detail.status).label}
                        </span>
                    </div>
                </div>
                <div class="card-body py-2">
                    <div class="row g-2 text-center">
                        <div class="col-3">
                            <div class="detail-meta-lbl">Category</div>
                            <div class="detail-meta-val">
                                {categoryOf(detail.category).label}
                            </div>
                        </div>
                        <div class="col-3">
                            <div class="detail-meta-lbl">Market</div>
                            <div class="detail-meta-val">
                                {MARKET[detail.marketType] ?? detail.marketType}
                            </div>
                        </div>
                        <div class="col-3">
                            <div class="detail-meta-lbl">Created</div>
                            <div class="detail-meta-val">
                                {fmt(detail.createdAt)}
                            </div>
                        </div>
                        <div class="col-3">
                            <div class="detail-meta-lbl">Messages</div>
                            <div class="detail-meta-val">
                                {detail.chats?.length ?? 0}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="row g-3">
                <!-- ── Chat + Reply + Actions ──────────────────────────────── -->
                <div class="col-md-7">
                    <!-- Chat history -->
                    <div class="card mb-3">
                        <div class="card-header py-2 fw-semibold">
                            Conversation
                        </div>
                        <div class="card-body p-2 chat-scroll">
                            {#if !detail.chats || detail.chats.length === 0}
                                <div class="text-center text-muted py-3">
                                    No messages yet
                                </div>
                            {:else}
                                {#each detail.chats as chat}
                                    <div
                                        class="d-flex mb-2 {chat.senderIsUser
                                            ? ''
                                            : 'flex-row-reverse'}"
                                    >
                                        <div
                                            class="chat-bubble {chat.senderIsUser
                                                ? 'bubble-user'
                                                : 'bubble-support'}"
                                        >
                                            <div class="chat-sender">
                                                {chat.senderIsUser
                                                    ? "User"
                                                    : "Support"}
                                            </div>
                                            <div class="chat-body">
                                                {chat.description}
                                            </div>
                                            <div class="chat-time">
                                                {fmt(chat.submittedAt)}
                                            </div>
                                            {#if chat.isNeededEmail}
                                                <span
                                                    class="badge bg-info mt-1"
                                                    style="font-size:0.62rem"
                                                >
                                                    Email sent
                                                </span>
                                            {/if}
                                        </div>
                                    </div>
                                {/each}
                            {/if}
                        </div>
                    </div>

                    <!-- Reply / Actions (only when ticket is open) -->
                    {#if detail.status !== 0}
                        <div class="card mb-3">
                            <div class="card-header py-2 fw-semibold">
                                Reply
                            </div>
                            <div class="card-body">
                                <textarea
                                    class="form-control mb-2"
                                    rows="3"
                                    placeholder="Write your reply…"
                                    bind:value={replyText}
                                ></textarea>

                                {#if readyAnswers.length > 0}
                                    <div class="card mt-3">
                                        <div
                                            class="card-header py-2 fw-semibold"
                                        >
                                            Ready Answers
                                        </div>

                                        <div
                                            class="card-body p-2 ready-answer-list"
                                        >
                                            {#each readyAnswers as answer}
                                                <div
                                                    class="ready-answer-item mb-2"
                                                    on:click={() => {
                                                        replyText +=
                                                            (replyText
                                                                ? "\n\n"
                                                                : "") + answer;
                                                    }}
                                                >
                                                    {answer}
                                                </div>
                                            {/each}
                                        </div>
                                    </div>
                                {/if}

                                <div
                                    class="d-flex justify-content-between align-items-center"
                                >
                                    <div class="form-check mb-0">
                                        <input
                                            class="form-check-input"
                                            type="checkbox"
                                            id="sendEmail"
                                            bind:checked={sendEmail}
                                        />
                                        <label
                                            class="form-check-label"
                                            for="sendEmail"
                                            style="font-size:0.85rem"
                                        >
                                            Send email notification
                                        </label>
                                    </div>
                                    <button
                                        class="btn btn-primary btn-sm"
                                        on:click={sendReply}
                                    >
                                        Send Reply
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div class="card">
                            <div class="card-header py-2 fw-semibold">
                                Actions
                            </div>
                            <div class="card-body">
                                <div class="row g-2">
                                    <div class="col-8">
                                        <select
                                            class="form-select form-select-sm"
                                            bind:value={newStatus}
                                        >
                                            {#each STATUS as s}
                                                <option value={String(s.value)}
                                                    >{s.label}</option
                                                >
                                            {/each}
                                        </select>
                                    </div>
                                    <div class="col-4">
                                        <button
                                            class="btn btn-warning btn-sm w-100"
                                            on:click={changeStatus}
                                        >
                                            Change Status
                                        </button>
                                    </div>
                                    <div class="col-12">
                                        <button
                                            class="btn btn-danger btn-sm w-100"
                                            on:click={closeTicket}
                                        >
                                            Close Ticket
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    {:else}
                        <div class="alert alert-secondary mb-0">
                            This ticket is closed. No further actions available.
                        </div>
                    {/if}
                </div>

                <!-- ── User Info ────────────────────────────────────────────── -->
                <div class="col-md-5">
                    {#if detail.userInfo}
                        <div class="card">
                            <div class="card-header py-2 fw-semibold">
                                Player Info
                            </div>
                            <div class="card-body p-0">
                                <table
                                    class="table table-sm table-borderless mb-0 user-table"
                                >
                                    <tbody>
                                        <tr>
                                            <td class="lbl">Username</td>
                                            <td class="val fw-semibold"
                                                >{detail.userInfo.username ??
                                                    "—"}</td
                                            >
                                        </tr>
                                        <tr>
                                            <td class="lbl">User ID</td>
                                            <td
                                                class="val font-monospace"
                                                style="font-size:0.7rem"
                                            >
                                                {detail.userInfo.id ?? "—"}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td class="lbl">Email</td>
                                            <td class="val"
                                                >{detail.userInfo.email ??
                                                    "—"}</td
                                            >
                                        </tr>
                                        <tr>
                                            <td class="lbl">Phone</td>
                                            <td class="val"
                                                >{detail.userInfo.phoneNumber ??
                                                    "—"}</td
                                            >
                                        </tr>
                                        <tr>
                                            <td class="lbl">Level</td>
                                            <td class="val"
                                                >{detail.userInfo.level ??
                                                    "—"}</td
                                            >
                                        </tr>
                                        <tr>
                                            <td class="lbl">Coins</td>
                                            <td class="val"
                                                >{detail.userInfo.coin?.toLocaleString() ??
                                                    "—"}</td
                                            >
                                        </tr>
                                        <tr>
                                            <td class="lbl">Tickets</td>
                                            <td class="val"
                                                >{detail.userInfo.ticket ??
                                                    "—"}</td
                                            >
                                        </tr>
                                        <tr>
                                            <td class="lbl">Clan</td>
                                            <td class="val"
                                                >{detail.userInfo.clanName ??
                                                    "—"}</td
                                            >
                                        </tr>
                                        <tr>
                                            <td class="lbl">Member since</td>
                                            <td class="val"
                                                >{fmt(
                                                    detail.userInfo.createdAt,
                                                )}</td
                                            >
                                        </tr>
                                        <tr>
                                            <td class="lbl">ClientVersion</td>
                                            <td class="val"
                                                >{detail.userInfo.clientVersion ??
                                                    "—"}</td
                                            >
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    {:else}
                        <div class="card">
                            <div class="card-body text-center text-muted">
                                No user info available
                            </div>
                        </div>
                    {/if}
                </div>
            </div>
        {/if}
    </div>
</div>

<style>
    /* Stat bar cards */
    .stat-card {
        background: #fff;
        border: 2px solid;
        border-radius: 0.5rem;
        padding: 0.75rem 1rem;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.2rem;
    }
    .stat-num {
        font-size: 2rem;
        font-weight: 700;
        line-height: 1;
    }
    .stat-lbl {
        font-size: 0.8rem;
        color: #6c757d;
    }

    /* Clickable hover */
    .clickable {
        cursor: pointer;
        transition: box-shadow 0.15s;
    }
    .clickable:hover {
        box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.2);
    }

    /* Ticket list items */
    .ticket-item {
        border-left: none;
        border-right: none;
    }
    .ticket-item.active {
        background-color: #0d6efd !important;
        border-color: #0d6efd;
        color: #fff;
    }
    .ticket-item.active .ticket-meta {
        color: rgba(255, 255, 255, 0.75) !important;
    }
    .ticket-title {
        font-size: 0.83rem;
        font-weight: 600;
        line-height: 1.3;
    }
    .ticket-meta {
        font-size: 0.7rem;
        color: #6c757d;
    }

    /* Chat bubbles */
    .chat-scroll {
        max-height: 360px;
        overflow-y: auto;
    }
    .chat-bubble {
        max-width: 82%;
        padding: 0.45rem 0.65rem;
        border-radius: 0.5rem;
    }
    .bubble-user {
        background: #e9ecef;
    }
    .bubble-support {
        background: #cfe2ff;
    }
    .chat-sender {
        font-size: 0.7rem;
        font-weight: 600;
        margin-bottom: 0.2rem;
        color: #495057;
    }
    .chat-body {
        font-size: 0.85rem;
        line-height: 1.4;
    }
    .chat-time {
        font-size: 0.68rem;
        color: #6c757d;
        margin-top: 0.25rem;
    }

    /* Detail meta row */
    .detail-meta-lbl {
        font-size: 0.72rem;
        color: #6c757d;
    }
    .detail-meta-val {
        font-size: 0.82rem;
        font-weight: 500;
    }

    /* User info table */
    .user-table td {
        padding: 0.3rem 0.75rem;
        vertical-align: middle;
    }
    .user-table .lbl {
        font-size: 0.75rem;
        color: #6c757d;
        white-space: nowrap;
        width: 45%;
    }
    .user-table .val {
        font-size: 0.78rem;
    }

    .ready-answer-list {
        max-height: 220px;
        overflow-y: auto;
    }

    .ready-answer-item {
        border: 1px solid #dee2e6;
        border-radius: 8px;
        padding: 10px 12px;
        cursor: pointer;
        background: #fafafa;
        transition: 0.15s;
        white-space: pre-wrap;
        font-size: 0.82rem;
    }

    .ready-answer-item:hover {
        background: #f0f7ff;
        border-color: #0d6efd;
        transform: translateX(3px);
    }
</style>
